/**
 * A saved intake template's editable state, the template counterpart of the
 * Requests editor's `useIntakeDraft`: a local copy of name, questions, visa,
 * description and the defaults every new intake starts with (note, expiry,
 * reminders, branding), saved to the template on a debounce. Edits reach the
 * next intake made from it; intakes already sent keep theirs. A newer copy
 * from elsewhere (Casey revising it) replaces the local one when there are no
 * unsaved edits.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { OutgoingIntakesAPI } from "@/api/outgoingIntakesApi";
import type {
  OutgoingIntakeFormNode,
  OutgoingIntakeSavedTemplate,
  OutgoingIntakeTemplateDefaults
} from "@/api/types/outgoingIntake";
import type { SaveState } from "@/features/requestsIde/editor/useIntakeDraft";

export type TemplateDraft = {
  name: string;
  schema: OutgoingIntakeFormNode[];
  visa: string | null;
  description: string;
  defaults: OutgoingIntakeTemplateDefaults;
  saveState: SaveState;
  setName: (name: string) => void;
  setSchema: (schema: OutgoingIntakeFormNode[]) => void;
  setVisa: (visa: string | null) => void;
  setDescription: (description: string) => void;
  setDefaults: (patch: OutgoingIntakeTemplateDefaults) => void;
  /** Save pending edits now (before sending, or when the editor closes). */
  flush: () => Promise<void>;
};

type Patch = {
  name?: string;
  schema?: OutgoingIntakeFormNode[];
  visa?: string | null;
  description?: string | null;
} & OutgoingIntakeTemplateDefaults;

const defaultsOf = (
  t: OutgoingIntakeSavedTemplate | null
): OutgoingIntakeTemplateDefaults => ({
  attorney_note: t?.attorney_note ?? null,
  expires_in_days: t?.expires_in_days ?? null,
  reminder_schedule: t?.reminder_schedule ?? null,
  enable_firm_branding: t?.enable_firm_branding ?? null
});

const SAVE_DEBOUNCE_MS = 900;

/** One version of a template: a change made elsewhere (Casey) has a new one. */
const versionOf = (t: OutgoingIntakeSavedTemplate): string =>
  `${t.template_id}@${JSON.stringify(t.updated_at ?? null)}`;

export const useTemplateDraft = (
  template: OutgoingIntakeSavedTemplate | null,
  onSaved: (template: OutgoingIntakeSavedTemplate) => void
): TemplateDraft => {
  const templateId = template?.template_id ?? null;
  const [name, setNameState] = useState(template?.name ?? "");
  const [schema, setSchemaState] = useState<OutgoingIntakeFormNode[]>(
    template?.schema ?? []
  );
  const [visa, setVisaState] = useState<string | null>(template?.visa ?? null);
  const [description, setDescriptionState] = useState(
    template?.description ?? ""
  );
  const [defaults, setDefaultsState] = useState(() => defaultsOf(template));
  const [saveState, setSaveState] = useState<SaveState>("saved");
  const pending = useRef<Patch>({});
  const timer = useRef<number | null>(null);
  const loadedVersion = useRef<string | null>(null);
  const savedVersion = useRef<string | null>(null);
  const saving = useRef(false);
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;

  // Adopt the template when it first arrives, another one opens, or it
  // changed elsewhere while there are no local edits to keep.
  useEffect(() => {
    if (!template) return;
    const version = versionOf(template);
    if (loadedVersion.current === version) return;
    const same =
      loadedVersion.current?.startsWith(`${template.template_id}@`) ?? false;
    loadedVersion.current = version;
    if (
      same &&
      (version === savedVersion.current ||
        saving.current ||
        Object.keys(pending.current).length)
    )
      return;
    pending.current = {};
    setNameState(template.name);
    setSchemaState(template.schema ?? []);
    setVisaState(template.visa ?? null);
    setDescriptionState(template.description ?? "");
    setDefaultsState(defaultsOf(template));
    setSaveState("saved");
  }, [template]);

  const flush = useCallback(async () => {
    if (timer.current) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    const patch = pending.current;
    if (!templateId || !Object.keys(patch).length) return;
    pending.current = {};
    setSaveState("saving");
    saving.current = true;
    try {
      const updated = await OutgoingIntakesAPI.updateSavedTemplate(
        templateId,
        patch
      );
      savedVersion.current = versionOf(updated);
      saving.current = false;
      onSavedRef.current(updated);
      setSaveState(Object.keys(pending.current).length ? "dirty" : "saved");
    } catch (e) {
      saving.current = false;
      // Edits made while this save was in flight are newer than the patch.
      pending.current = { ...patch, ...pending.current };
      setSaveState("error");
      toast.error(
        e instanceof Error ? e.message : "Couldn't save the template"
      );
    }
  }, [templateId]);

  const schedule = useCallback(
    (patch: Patch) => {
      Object.assign(pending.current, patch);
      setSaveState("dirty");
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => void flush(), SAVE_DEBOUNCE_MS);
    },
    [flush]
  );

  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
      if (Object.keys(pending.current).length) void flush();
    },
    [flush]
  );

  const setName = useCallback(
    (next: string) => {
      setNameState(next);
      if (next.trim()) schedule({ name: next.trim() });
    },
    [schedule]
  );
  const setSchema = useCallback(
    (next: OutgoingIntakeFormNode[]) => {
      setSchemaState(next);
      schedule({ schema: next });
    },
    [schedule]
  );
  const setVisa = useCallback(
    (next: string | null) => {
      setVisaState(next);
      schedule({ visa: next });
    },
    [schedule]
  );
  const setDescription = useCallback(
    (next: string) => {
      setDescriptionState(next);
      schedule({ description: next.trim() || null });
    },
    [schedule]
  );
  const setDefaults = useCallback(
    (patch: OutgoingIntakeTemplateDefaults) => {
      setDefaultsState((current) => ({ ...current, ...patch }));
      schedule(patch);
    },
    [schedule]
  );

  return useMemo(
    () => ({
      name,
      schema,
      visa,
      description,
      defaults,
      saveState,
      setName,
      setSchema,
      setVisa,
      setDescription,
      setDefaults,
      flush
    }),
    [
      name,
      schema,
      visa,
      description,
      defaults,
      saveState,
      setName,
      setSchema,
      setVisa,
      setDescription,
      setDefaults,
      flush
    ]
  );
};
